import type { PumsemDivisionFacet } from '../model/pumsem.type';

interface PumsemFacetsProps {
  facets: PumsemDivisionFacet[];
  division: string | null;
  onDivisionChange: (division: string | null) => void;
  resource: string | null;
  onResourceClear: () => void;
  changedOnly: boolean;
  onChangedOnlyChange: (value: boolean) => void;
  editionLabel: string;
}

const CHIP =
  'flex h-7 items-center rounded-full border px-3 text-[13.5px] transition-colors';
const CHIP_ON =
  'border-brand-500 bg-brand-500 font-bold text-white dark:border-brand-500';
const CHIP_OFF =
  'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 dark:border-white/12 dark:bg-transparent dark:text-neutral-300 dark:hover:bg-white/5';

/**
 * 부문 패싯 + 개정분 필터 + 역검색 표시.
 *
 * 건수는 서버가 준 값을 그대로 쓴다. 결과 배열에서 세면 페이지네이션된 30건만
 * 세게 되고, 첫 페이지에 안 나온 부문은 칩조차 안 생겨 고를 수가 없다.
 */
export function PumsemFacets({
  facets,
  division,
  onDivisionChange,
  resource,
  onResourceClear,
  changedOnly,
  onChangedOnlyChange,
  editionLabel,
}: PumsemFacetsProps) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* 선택 상태가 배경색으로만 표현되면 인쇄물과 색약 사용자에게 아무
          정보가 없다. 바로 옆 `개정분만` 칩은 이미 `aria-pressed` 를 단다. */}
      <button
        type="button"
        aria-pressed={division === null}
        onClick={() => onDivisionChange(null)}
        className={`${CHIP} ${division === null ? CHIP_ON : CHIP_OFF}`}
      >
        전체
      </button>
      {facets.map((item) => (
        <button
          key={item.slug}
          type="button"
          aria-pressed={division === item.slug}
          onClick={() => onDivisionChange(item.slug)}
          className={`${CHIP} ${division === item.slug ? CHIP_ON : CHIP_OFF}`}
        >
          {item.shortTitle}
          <span
            className={`ml-1.5 ${division === item.slug ? 'text-white/75' : 'text-neutral-400'}`}
          >
            {item.count}
          </span>
        </button>
      ))}

      <span className="mx-1 h-4 w-px bg-neutral-200 dark:bg-white/12" />

      <button
        type="button"
        aria-pressed={changedOnly}
        onClick={() => onChangedOnlyChange(!changedOnly)}
        className={`${CHIP} ${changedOnly ? CHIP_ON : `${CHIP_OFF} border-dashed`}`}
      >
        {editionLabel} 개정분만
      </button>

      {/* 역검색은 사용자가 명시적으로 켠 게 아니라 결과 카드의 직종을 눌러 켜진다.
          어떤 조건이 걸려 있는지 보이지 않으면 "왜 결과가 이것뿐이지"가 된다. */}
      {resource && (
        <button
          type="button"
          onClick={onResourceClear}
          className={`${CHIP} border-brand-200 bg-brand-50 font-bold text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300`}
        >
          직종 · {resource}
          <span aria-hidden className="ml-1.5 text-brand-500">
            ✕
          </span>
          <span className="sr-only">직종 조건 해제</span>
        </button>
      )}
    </div>
  );
}
